import { Action } from '@ngrx/store';
import {
  merge,
  Notification,
  Observable
} from 'rxjs';
import {
  ignoreElements,
  map,
  materialize,
  catchError
} from 'rxjs/operators';

import { ErrorHandler } from '@angular/core';
import { FormNotification } from './form-notification';
import { FORM_EFFECT_METADATA_KEY } from './decorators/metadata-keys';
import { DxFormEffect } from './decorators/interfaces';
import { getMetadata } from '@digitaix/helpers';

export function getFormEffects(
  sourceInstance: any
): Array<{
  propertyKey: string;
  dispatch: boolean;
  resubscribeOnError: boolean;
}> {
  const formEffects = getMetadata<DxFormEffect[]>(
    FORM_EFFECT_METADATA_KEY,
    Object.getPrototypeOf(sourceInstance)
  );

  return formEffects.map(fe => ({
    ...fe,
    propertyKey: fe.propertyKey,
  }));
}

export function mergeFormEffects(
  sourceInstance: any,
  errorHandler: ErrorHandler
): Observable<FormNotification> {
  const sourceName = Object.getPrototypeOf(sourceInstance).constructor.name;

  const observables$: Observable<any>[] = getFormEffects(sourceInstance).map(
    ({
      propertyKey,
      dispatch,
      resubscribeOnError
    }): Observable<FormNotification> => {
      const observable$: Observable<any> =
              typeof sourceInstance[propertyKey] === 'function'
          ? sourceInstance[propertyKey]()
          : sourceInstance[propertyKey];

      const resubscribable$ = resubscribeOnError
        ? observable$.pipe(
            catchError(error => {
              if (errorHandler) errorHandler.handleError(error);
              // Return observable that produces this particular effect
              return observable$;
            })
          )
        : observable$;

      if (dispatch === false) {
        return resubscribable$.pipe(ignoreElements());
      }

      const materialized$ = resubscribable$.pipe(materialize());

      return materialized$.pipe(
        map(
          (notification: Notification<Action>): FormNotification => ({
            effect:      sourceInstance[propertyKey],
            notification,
            propertyName: propertyKey,
            sourceName,
            sourceInstance
          })
        )
      );
    }
  );

  return merge(...observables$);
}
